import { Box, DataList, Flex, Separator, Table } from '@volue/wave-react';
import React from 'react';

import { EvaluatedResultComponent } from '@/features/execute-calculation-set/components/EvaluatedResultComponent';
import { EvaluatedValue } from '@/features/execute-calculation-set/components/MediumVoltageCalculation/LoadFlowSummaryCalculationResults/EvaluatedCalculationResult';
import type { SelectedGridElement } from '@/features/execute-calculation-set/overmind/calculation';
import type { BusbarShortCircuitDetails } from '@/features/execute-calculation-set/overmind/calculation/derived-state/gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';
import type { ElectricCurrentType } from '@/features/execute-calculation-set/overmind/calculation/domain/ElectricCurrent';
import type { EvaluatedResult } from '@/features/execute-calculation-set/overmind/calculation/evaluation/EvaluatedResult';
import { useAppState } from '@/overmind';

export type SelectedBusbar = {
  type: 'SELECTED_GRID_ELEMENT';
  gridElement: SelectedGridElement<'Busbar'>;
};

export const BusbarDetails: React.FunctionComponent<{
  busbar: SelectedBusbar;
}> = ({ busbar }) => {
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;
  const getLoadSourceValue = (loadSource?: string) =>
    loadSource == null ? '' : `(${loadSource})`;

  return (
    <div data-testid={`busbar-${busbar.gridElement.objectId}`}>
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'ALL_LOAD_FLOW' ||
        tacticResultBrowsingFilter.type === 'SELECTED_LOAD_FLOW') &&
        busbar.gridElement.loadflow.type ===
          'ALL_LOAD_FLOW_SCENARIO_DETAILS' && (
          <>
            <Table aria-label="Load scenarios" highlightOnHover={false}>
              <Table.Header>
                <Table.ColumnHeader>{'Scenario'}</Table.ColumnHeader>
                <>
                  {busbar.gridElement.loadflow.details.map(scenario => (
                    <Table.ColumnHeader>
                      {scenario.scenarioName}
                    </Table.ColumnHeader>
                  ))}
                </>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Voltage</Box>
                  </Flex>
                  <>
                    {busbar.gridElement.loadflow.details.map(scenario => (
                      <Table.Cell>
                        <EvaluatedResultComponent
                          result={scenario.scenarioDetails.results.voltage}
                        />
                      </Table.Cell>
                    ))}
                  </>
                </Table.Row>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Voltage Drop</Box>
                  </Flex>
                  <>
                    {busbar.gridElement.loadflow.details.map(scenario => (
                      <Table.Cell>
                        <EvaluatedResultComponent
                          result={
                            scenario.scenarioDetails.results.voltageDiffPercent
                          }
                          unitLabel
                        />
                      </Table.Cell>
                    ))}
                  </>
                </Table.Row>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Power Factor</Box>
                  </Flex>
                  <>
                    {busbar.gridElement.loadflow.details.map(scenario => (
                      <Table.Cell>
                        <EvaluatedResultComponent
                          result={
                            scenario.scenarioDetails.results.voltageCosinePhi
                          }
                        />
                      </Table.Cell>
                    ))}
                  </>
                </Table.Row>
              </Table.Body>
            </Table>
            <Separator />
            <Table aria-label="Load scenarios" highlightOnHover={false}>
              <Table.Body>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Active Power</Box>
                  </Flex>
                  <>
                    {busbar.gridElement.loadflow.details.map(scenario => (
                      <Table.Cell className="dIB">
                        <Box>
                          {scenario.scenarioDetails.inputs.p.toString()}
                        </Box>
                        <Box>
                          {getLoadSourceValue(
                            scenario.scenarioDetails.inputs.loadSource
                          )}
                        </Box>
                      </Table.Cell>
                    ))}
                  </>
                </Table.Row>
                <Table.Row>
                  <Flex
                    as={Table.Cell}
                    flow="column"
                    gap={'none'}
                    justifyContent="start"
                    alignItems="start"
                  >
                    <Box>Reactive Power</Box>
                  </Flex>
                  <>
                    {busbar.gridElement.loadflow.details.map(scenario => (
                      <Table.Cell className="dIB">
                        <Box>
                          {scenario.scenarioDetails.inputs.q.toString()}
                        </Box>
                        <Box>
                          {getLoadSourceValue(
                            scenario.scenarioDetails.inputs.loadSource
                          )}
                        </Box>
                      </Table.Cell>
                    ))}
                  </>
                </Table.Row>
              </Table.Body>
            </Table>
          </>
        )}
      {(tacticResultBrowsingFilter.type === 'ALL' ||
        tacticResultBrowsingFilter.type === 'SHORT_CIRCUIT') &&
        busbar.gridElement.shortCircuit.type === 'SHORT_CIRCUIT_DETAILS' && (
          <>
            {busbar.gridElement.shortCircuit.type ===
              'SHORT_CIRCUIT_DETAILS' && (
              <Flex flow="column" gap={'spacingXs'}>
                <Box css={{ color: '$colorAccent70', fontWeight: 'bolder' }}>
                  {'Short Circuit'}
                </Box>
                {busbar.gridElement.objectType === 'Busbar' &&
                  busbar.gridElement.shortCircuit.evaluatedResults
                    .objectType === 'Busbar' && (
                    <DataList className="mHxl" ratio={[3, 1]}>
                      <Phase1Min
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                      <Phase1Max
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                      <Phase2Min
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                      <Phase3Max
                        results={
                          busbar.gridElement.shortCircuit.evaluatedResults
                            .results
                        }
                      />
                    </DataList>
                  )}
              </Flex>
            )}
          </>
        )}
    </div>
  );
};

function EvaluatedDataListItem({
  label,
  value
}: {
  label: string;
  value: EvaluatedResult<ElectricCurrentType>;
}): JSX.Element | null {
  return (
    <Flex as={DataList.Row} main="space-between">
      <DataList.Label>{label}</DataList.Label>
      <Flex main={'end'} as={DataList.Value}>
        <EvaluatedValue value={value.value.toString()} status={value.status} />
      </Flex>
    </Flex>
  );
}

function Phase1Min({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Min" value={results.phase1Min.result} />
  ) : null;
}

function Phase1Max({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase1Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase1Max" value={results.phase1Max.result} />
  ) : null;
}

function Phase2Min({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase2Min.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase2Min" value={results.phase2Min.result} />
  ) : null;
}

function Phase3Max({
  results
}: {
  results: BusbarShortCircuitDetails['results'];
}): JSX.Element | null {
  return results.phase3Max.type === 'CALCULATION_RESULT_AVAILABLE' ? (
    <EvaluatedDataListItem label="Phase3Max" value={results.phase3Max.result} />
  ) : null;
}
